import React from 'react';
import { Clock, LucideProps } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

interface ComingSoonModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureName?: string;
  description?: string;
  IconComponent?: React.ForwardRefExoticComponent<
    Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>
  >;
}

/**
 * Modal that displays a "coming soon" message for features not yet implemented
 *
 * @example
 * ```tsx
 * import { ComingSoonModal } from '../components/ComingSoonModal';
 *
 * // Basic usage
 * <ComingSoonModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 * />
 *
 * // With custom content
 * <ComingSoonModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   featureName="Dark mode"
 *   description="We're working on a beautiful dark theme for better night-time browsing!"
 *   icon="rocket"
 * />
 * ```
 */
export const ComingSoonModal: React.FC<ComingSoonModalProps> = ({
  isOpen,
  onClose,
  featureName = 'This feature',
  description = "We're working hard to bring this feature to you. Stay tuned!",
  IconComponent = Clock,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-amber-50 [--mb-surface:var(--color-amber-50)] border-amber-200 rounded-2xl">
        <DialogHeader className="sr-only">
          <DialogTitle>Coming Soon</DialogTitle>
          <DialogDescription>
            {featureName} is coming soon. {description}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center py-6">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <IconComponent className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {featureName} is on the way!
            </h3>
            <p className="text-sm text-amber-700 max-w-xs">{description}</p>
          </div>
        </div>

        <div className="flex justify-center pt-4">
          <Button
            onClick={onClose}
            className="bg-amber-500 hover:bg-amber-600 text-mb-ink rounded-xl px-8"
          >
            Got it!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
